import React, { useState } from 'react';
import { 
  Layout, 
  Form, 
  Input, 
  InputNumber, 
  Select, 
  Button, 
  Upload, 
  Checkbox, 
  Alert, 
  Typography,
  Row,
  Col,
  Grid,
  Space,
  message,
  DatePicker,
  Divider,
  Card
} from 'antd';
import { 
  ArrowLeftOutlined,
  UploadOutlined,
  FileTextOutlined,
  UserOutlined,
  DollarOutlined,
  SafetyOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useClientAdmin } from '../../context/ClientAdminContext';
import { API_ORIGIN } from '../../config';

const { Content, Header } = Layout;
const { Title, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const RockNMalta = () => {
  const navigate = useNavigate();
  const { clientAdmin } = useClientAdmin();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [eventDescWordCount, setEventDescWordCount] = useState(0);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  // Debug: Log the clientAdmin object
  const screens = Grid.useBreakpoint();
  const isSmall = !screens.md; // true for small screens (sm and xs)

  const maxWords = 500;

  const formFontColour = '#FFAC00';

  // Handle category selection change
  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    // Clear the dynamic field when category changes
    form.setFieldsValue({ category_applicant_name: '' });
  };

  // Get the field name based on category - now always the same
  const getDynamicFieldName = (category) => {
    return 'category_applicant_name';
  };

  // Get the field label based on category
  const getDynamicFieldLabel = (category) => {
    switch (category) {
      case 'Concert Organiser(s)':
        return 'Organiser Name';
      case 'Festival Promoter(s)':
        return 'Festival Promoter Name';
      case 'Band(s)':
        return 'Band Name';
      case 'Singer(s)':
        return 'Artist/Singer Name';
      case 'NGO(s)':
        return 'NGO Name';
      case 'Registered Voluntary Organisation(s)':
        return 'Voluntary Organisation Name';
      default:
        return '';
    }
  };

  const formId = 1;
  
  //Get specific activity status
  const [isFormActive, setIsFormActive] = useState(true); // Default to true to show form initially

  // Fetch form activity status on component mount
  React.useEffect(() => {
    const fetchFormStatus = async () => {
      try {
        const response = await fetch(`${API_ORIGIN}/api/clientadmin/specificForm?id=${formId}`, {
          method: 'GET',
        });
        const data = await response.json();
        
        if (response.ok && data.success) {
          // Backend returns 0 or 1 (tinyint) - convert to boolean
          const isActive = data.activeForm === 1 || data.isActive === 1;
          setIsFormActive(isActive);
        } else {
          setIsFormActive(false); // Disable form if fetch fails
        }
      } catch (error) {
        setIsFormActive(false); // Disable form on error
      }
    };

    fetchFormStatus();
  }, []);

  // Handle word count for event description (max 500 words)
  const handleEventDescChange = (e) => {
    const text = e.target.value;
    const words = text.match(/\b\w+\b/g) || [];
    const currentWordCount = words.length;
    
    if (currentWordCount > maxWords) {
      const trimmedText = words.slice(0, maxWords).join(' ');
      form.setFieldsValue({ event_description: trimmedText });
      setEventDescWordCount(maxWords);
      message.warning(`Event description is limited to ${maxWords} words`);
    } else {
      setEventDescWordCount(currentWordCount);
    }
  };

  // File upload props - Standard documents (5MB limit)
  const uploadProps = {
    beforeUpload: (file) => {
      const isPdf = file.type === 'application/pdf';
      const isImage = file.type.startsWith('image/');
      if (!isPdf && !isImage) {
        message.error('You can only upload PDF or image files!');
        return false;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('File must be smaller than 5MB!');
        return false;
      }
      return false; // Prevent auto upload
    },
    multiple: false,
    maxCount: 1,
  };

  // Business Plan documents upload props (50MB limit)
  const businessPlanUploadProps = {
    beforeUpload: (file) => {
      const isPdf = file.type === 'application/pdf';
      const isImage = file.type.startsWith('image/');
      if (!isPdf && !isImage) {
        message.error('You can only upload PDF or image files!');
        return false;
      }
      const isLt50M = file.size / 1024 / 1024 < 50;
      if (!isLt50M) {
        message.error('File must be smaller than 50MB!');
        return false;
      }
      return false; // Prevent auto upload
    },
    multiple: false,
    maxCount: 1,
  };

  const idCardUploadProps = {
    beforeUpload: (file) => {
      const isPdf = file.type === 'application/pdf';
      const isImage = file.type.startsWith('image/');
      if (!isPdf && !isImage) {
        message.error('You can only upload PDF or image files!');
        return false;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('File must be smaller than 5MB!');
        return false;
      }
      return false; // Prevent auto upload
    },
    multiple: true,
    maxCount: 2, // For ID card front & back
  };

  const onFinish = async (values) => {
    setLoading(true);
    setError('');
    
    try {
      // Create FormData for file uploads
      const formData = new FormData();
      
      // Validate required declarations explicitly (extra safety)
      if (!values.declare_not_undertaking_difficulty) {
        throw new Error('You must confirm applicants are not undertakings in difficulty');
      }
      if (!values.declare_no_outstanding_recovery) {
        throw new Error('You must confirm there are no outstanding recovery orders');
      }
      if (!values.declaration_operate) {
        throw new Error('You must confirm you are legally entitled to operate in Malta');
      }

      // Add all form fields
      Object.keys(values).forEach(key => {
        if (key === 'applicant_id_card' || key === 'tax_compliance_doc' || key === 'state_aid_declaration' || key === 'organisation_role_chart' || key === 'business_financial_model' || key === 'ticket_prices' || key === 'revenue_streams' || key === 'expenses_costs' || key === 'profit_loss_report' || key === 'anticipated_audience' || key === 'insurance_declaration' || key === 'project_management_plan' || key === 'marketing_plan') {
          // Handle file uploads - Fix the file access
          if (values[key] && Array.isArray(values[key])) {
            values[key].forEach((file) => {
              if (file && file.originFileObj) {
                formData.append(key, file.originFileObj);
              }
            });
          }
        } else if (key === 'declare_health_safety' || key === 'soft_copy_submitted' || key === 'declare_not_undertaking_difficulty' || key === 'declare_no_outstanding_recovery' || key === 'declaration_operate') {
          // Handle checkboxes - convert boolean to '1' or '0'
          const checkboxValue = values[key] === true ? '1' : '0';
          formData.append(key, checkboxValue);
          //(`Checkbox ${key}:`, values[key], '-> sending:', checkboxValue);
        } else if (key === 'event_date') {
          // Handle date field - convert to YYYY-MM-DD format
          if (values[key]) {
            const formattedDate = values[key].format('YYYY-MM-DD');
            formData.append(key, formattedDate);
          }
        } else if (key === 'estimated_cost') {
          // Handle estimated_cost as decimal - preserve decimal precision
          const costValue = values[key];
          if (costValue !== null && costValue !== undefined) {
            formData.append(key, costValue.toString());
          } else {
            formData.append(key, '0');
          }
        } else {
          // Handle all other fields
          formData.append(key, values[key] || '');
        }
      });

      // Ensure category_applicant_name is always sent (even if empty)
      if (!formData.has('category_applicant_name')) {
        formData.append('category_applicant_name', '');
      }

      // Explicitly ensure the new checkboxes are included even if undefined
      if (!formData.has('declare_not_undertaking_difficulty')) {
        formData.append('declare_not_undertaking_difficulty', values.declare_not_undertaking_difficulty ? '1' : '0');
      }
      if (!formData.has('declare_no_outstanding_recovery')) {
        formData.append('declare_no_outstanding_recovery', values.declare_no_outstanding_recovery ? '1' : '0');
      }

      // Add hard_copy_submitted as '0' (since it's disabled)
      formData.append('hard_copy_submitted', '0');

      // Debug: Log the FormData contents
      
      // Submit to your backend API
            const response = await fetch(`${API_ORIGIN}/api/rock-malta-fund/submit-application`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || `HTTP error! status: ${response.status}`);
      }

      if (result.success) {
        setSuccess(true);
        form.resetFields();
        setEventDescWordCount(0);
        setSelectedCategory(''); // Reset category selection
        message.success(result.message || 'Application submitted successfully!');
        
        // Scroll to top of page to show success message
        window.scrollTo({ top: 0, behavior: 'smooth' });
        
        // Show application ID if provided
        if (result.applicationId) {
          message.info(`Application ID: ${result.applicationId}`);
        }
      } else {
        throw new Error(result.error || 'Submission failed');
      }
    } catch (err) {
      setError(err.message || 'An error occurred while submitting your application');
      message.error('Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        /* Smooth transitions for buttons */
        .ant-btn {
          transition: transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease, border-color 180ms ease, color 180ms ease;
        }

        /* Hover colour for all non-primary buttons (scoped) */
        .ant-btn:not(.rnm-primary):hover {
          background-color: ${formFontColour} !important;
          border-color: ${formFontColour} !important;
          color: #fff !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 8px 20px rgba(255,172,0,0.12) !important;
        }

        /* Primary submit button base style */
        .rnm-primary {
          background-color: ${formFontColour} !important;
          border-color: ${formFontColour} !important;
          color: #fff !important;
          transform: translateY(0);
          box-shadow: 0 6px 18px rgba(24,144,255,0.08);
        }

        /* Primary submit hover animation */
        .rnm-primary:hover {
          transform: translateY(-4px) !important;
          box-shadow: 0 14px 36px rgba(255,172,0,0.22) !important;
        }

        .rnm-primary:active {
          transform: translateY(-1px) !important;
        }
      `}</style>
      <Layout style={{ minHeight: '100vh', background: '#fafafa', overflowX: 'hidden' }}>
      <Header style={{ 
        padding: '12px 16px', 
        background: '#fff', 
        borderBottom: '1px solid #f0f0f0',
        height: 'auto',
        overflowX: 'hidden'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          alignItems: 'center', 
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ 
            flex: '0 0 auto',
            display: 'flex', 
            justifyContent: 'flex-end',
            alignItems: 'center',
            paddingRight: isSmall ? 8 : 0
          }}>
            <img
              src="/images/FM_Logo.png"
              alt="Festivals Malta"
              onClick={() => navigate('/')}
              style={{
                height: isSmall ? '32px' : '36px',
                width: 'auto',
                maxWidth: '160px',
                cursor: 'pointer',
                objectFit: 'contain',
              }}
            />
          </div>
        </div>
      </Header>

      <Content style={{ padding: 'clamp(20px, 5vw, 48px)', overflowX: 'hidden' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: isSmall ? '0 12px' : '0 20px', width: '100%', boxSizing: 'border-box' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(24px, 6vw, 48px)' }}>
             <img
              src="/images/Rocknmalta_Logo.png"
              alt="Rock'N Malta"
              style={{
                height: isSmall ? '48px' : 'clamp(60px, 15vw, 100px)',
                maxWidth: '100%',
                maxHeight: '120px',
                objectFit: 'contain'
              }}
            />
            <Title level={1} style={{ color: '#222', letterSpacing: '1px', marginBottom: '16px', fontSize: 'clamp(24px, 6vw, 36px)' }}>
              Rock'N Malta Fund 2026 Application
            </Title>
            <Paragraph style={{ color: '#555', fontSize: 'clamp(14px, 4vw, 18px)' }}>
              Apply for funding and opportunities below.
            </Paragraph>
          </div>

          {success && (
            <Alert
              message="Application submitted successfully!"
              description="A representative will get back to you once it is vetted."
              type="success"
              style={{ marginBottom: '24px' }}
              closable
              onClose={() => setSuccess(false)}
            />
          )}

          {error && (
            <Alert
              message={error}
              type="error"
              style={{ marginBottom: '24px' }}
              closable
              onClose={() => setError('')}
            />
          )}

          <Card 
            style={{ 
              borderRadius: '12px', 
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: '1px solid #f0f0f0',
              maxWidth: '100%',
              overflowX: 'hidden'
            }}
            bodyStyle={{ padding: isSmall ? 20 : 40 }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              requiredMark={false}
              style={{ maxWidth: '100%', overflowX: 'hidden' }}
              disabled={!isFormActive}
            >
              <Row gutter={isSmall ? 12 : 24} style={{ maxWidth: '100%' }}>
                {/* Applicant Information Section */}
                <Col xs={24}>
                  {isSmall ? (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginTop: '8px', marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: 10 }}>
                      <UserOutlined style={{ fontSize: '20px', color: formFontColour, marginTop: 4 }} />
                      <div style={{ color: formFontColour, fontWeight: 700, fontSize: '16px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                        Applicant Information
                      </div>
                    </div>
                  ) : (
                    <Divider orientation="left" style={{ marginBottom: '24px' }}>
                      <Space style={{ alignItems: 'center', gap: 12 }}>
                        <UserOutlined style={{ fontSize: '20px', color: formFontColour }} />
                        <span style={{ fontSize: '20px', fontWeight: 700, color: formFontColour, display: 'inline-block', marginLeft: 6 }}>
                          Applicant Information
                        </span>
                      </Space>
                    </Divider>
                  )}
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="Name of Organisation"
                    name="organisation_name"
                    rules={[{ required: true, message: 'Please enter organisation name' }]}
                  >
                    <Input placeholder="Enter organisation name" />
                  </Form.Item>
                </Col>
                
                <Col xs={24} md={12}>
                  <Form.Item
                    label="Name of Representative"
                    name="representative_name"
                    rules={[{ required: true, message: 'Please enter representative name' }]}
                  >
                    <Input placeholder="Enter representative name" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="Working Name of Event"
                    name="applicant_name"
                    rules={[{ required: true, message: 'Please enter applicant name' }]}
                  >
                    <Input placeholder="Enter applicant name" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="Copy of ID Card (Front & Back)"
                    name="applicant_id_card"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => e?.fileList}
                    rules={[{ required: true, message: 'Please upload ID card' }]}
                  >
                      <Upload {...idCardUploadProps}>
                      <Button icon={<UploadOutlined />} style={{ width: isSmall ? '100%' : undefined }}>Upload ID Card (2 files max)</Button>
                    </Upload>
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="Contact Number"
                    name="contact_number"
                    rules={[{ required: true, message: 'Please enter contact number' }]}
                  >
                    <Input placeholder="Enter contact number" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="Total Estimated Cost (€)"
                    name="estimated_cost"
                    rules={[{ required: true, message: 'Please enter estimated cost' }]}
                  >
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={0.01}
                      placeholder="Enter amount"
                      addonBefore="€"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="Email address"
                    name="email_address"
                    rules={[
                      { required: true, message: 'Please enter email address' },
                      { type: 'email', message: 'Please enter a valid email address' }
                    ]}
                  >
                    <Input placeholder="Enter email address" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="Category of applicant"
                    name="category_of_applicant"
                    rules={[{ required: true, message: 'Please select category' }]}
                  >
                    <Select placeholder="Select category" onChange={handleCategoryChange}>
                      <Option value="Concert Organiser(s)">Concert Organiser</Option>
                      <Option value="Festival Promoter(s)">Festival Promoter</Option>
                      <Option value="Band(s)">Band</Option>
                      <Option value="Singer(s)">Singer</Option>
                      <Option value="NGO(s)">NGO</Option>
                      <Option value="Registered Voluntary Organisation(s)">Registered Voluntary Organisation</Option>
                    </Select>
                  </Form.Item>
                </Col>

                {/* Dynamic field based on category selection */}
                {selectedCategory && (
                  <Col xs={24} md={12}>
                    <Form.Item
                      label={getDynamicFieldLabel(selectedCategory)}
                      name={getDynamicFieldName(selectedCategory)}
                      rules={[{ required: true, message: `Please enter ${getDynamicFieldLabel(selectedCategory).toLowerCase()}` }]}
                    >
                      <Input placeholder={`Enter ${getDynamicFieldLabel(selectedCategory).toLowerCase()}`} />
                    </Form.Item>
                  </Col>
                )}

                <Col xs={24}>
                  <Form.Item
                    label="Please upload a copy of the applicant’s tax compliance certificate (5mb) (jpg / pdf)"
                    name="tax_compliance_doc"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => e?.fileList}
                    rules={[{ required: true, message: 'Please upload tax compliance document' }]}
                  >
                    <Upload {...uploadProps}>
                      <Button icon={<UploadOutlined />} style={{ width: isSmall ? '100%' : undefined }}>Upload Document</Button>
                    </Upload>
                  </Form.Item>
                </Col>

                {/* Event Details Section */}
                <Col xs={24}>
                  {isSmall ? (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginTop: '24px', marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: 10 }}>
                      <FileTextOutlined style={{ fontSize: '20px', color: formFontColour, marginTop: 4 }} />
                      <div style={{ color: formFontColour, fontWeight: 700, fontSize: '16px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                        Event Details
                      </div>
                    </div>
                  ) : (
                    <Divider orientation="left" style={{ marginTop: '32px', marginBottom: '24px' }}>
                      <Space style={{ alignItems: 'center', gap: 12 }}>
                        <FileTextOutlined style={{ fontSize: '20px', color: formFontColour }} />
                        <span style={{ fontSize: '20px', fontWeight: 700, color: formFontColour, display: 'inline-block', marginLeft: 6 }}>
                          Event Details
                        </span>
                      </Space>
                    </Divider>
                  )}
                </Col>

                <Col xs={24}>
                  <Form.Item
                      label={
                      <Space>
                        <span>Event Description</span>
                        <span style={{ color: '#999', fontSize: '0.9em' }}>(Maximum 500 words)</span>
                      </Space>
                    }
                    name="event_description"
                    rules={[{ required: true, message: 'Please enter event description' }]}
                  >
                    <TextArea 
                      rows={4} 
                      placeholder="Describe your event (max 500 words)"
                      onChange={handleEventDescChange}
                    />
                  </Form.Item>
                  <div style={{ textAlign: 'right', color: '#666', fontSize: '0.9em', marginTop: '-16px', marginBottom: '16px' }}>
                    {eventDescWordCount} / {maxWords} words
                  </div>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    label="Artists proposed (a minimum of 30% of the artists must be local)"
                    name="artists_proposed"
                    rules={[{ required: true, message: 'Please enter proposed artists' }]}
                  >
                    <TextArea rows={4} placeholder="List the proposed artists" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="Venue Proposed"
                    name="venue_proposed"
                    rules={[{ required: true, message: 'Please enter proposed venue' }]}
                  >
                    <Input placeholder="Enter proposed venue" />
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    label="Date of Proposed Event"
                    name="event_date"
                    rules={[{ required: true, message: 'Please select event date' }]}
                  >
                    <DatePicker 
                      style={{ width: '100%' }} 
                      format="DD/MM/YYYY"
                      placeholder="Select event date"
                    />
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    label="Please upload an organisation / Role chart including the names of those occupying each role"
                    name="organisational_role_chart"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                    rules={[{ required: true, message: 'Please upload organisation/role chart' }]}
                  >
                    <Upload {...uploadProps}>
                      <Button icon={<UploadOutlined />} style={{ width: isSmall ? '100%' : undefined }}>Upload Organisation/Role Chart</Button>
                    </Upload>
                  </Form.Item>
                </Col>

                {/* Business Plan Section - Required File Uploads */}
                <Col xs={24}>
                  {isSmall ? (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginTop: '24px', marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: 10 }}>
                      <DollarOutlined style={{ fontSize: '20px', color: formFontColour, marginTop: 4 }} />
                      <div style={{ color: formFontColour, fontWeight: 700, fontSize: '16px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                        Business Plan (Clause 4.e items) - Required Documents
                      </div>
                    </div>
                  ) : (
                    <Divider orientation="left" style={{ marginTop: '32px', marginBottom: '24px' }}>
                      <Space style={{ alignItems: 'center', gap: 12 }}>
                        <DollarOutlined style={{ fontSize: '20px', color: formFontColour }} />
                        <span style={{ fontSize: '20px', fontWeight: 700, color: formFontColour, display: 'inline-block', marginLeft: 6 }}>
                          Business Plan (Clause 4.e items) - Required Documents
                        </span>
                      </Space>
                    </Divider>
                  )}
                </Col>

                <Col xs={24}>
                  <Form.Item
                    label={<div style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}><span style={{ color: formFontColour, fontWeight: '600' }}>i.</span> Please upload Business Plan and Financial Model for the event</div>}
                    name="business_financial_model"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                    rules={[{ required: true, message: 'Please upload business and financial model' }]}
                  >
                    <Upload {...businessPlanUploadProps} style={{ display: 'block', textAlign: 'left' }}>
                      <Button icon={<UploadOutlined />} style={{ borderRadius: '6px', width: isSmall ? '100%' : undefined }}>Upload Business and Financial Model (Max 50MB)</Button>
                    </Upload>
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    label={<div style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}><span style={{ color: formFontColour, fontWeight: '600' }}>ii.</span> Please upload Ticket Prices (including packages & concessions)</div>}
                    name="ticket_prices"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                    rules={[{ required: true, message: 'Please upload ticket prices document' }]}
                  >
                    <Upload {...businessPlanUploadProps} style={{ display: 'block', textAlign: 'left' }}>
                      <Button icon={<UploadOutlined />} style={{ borderRadius: '6px', width: isSmall ? '100%' : undefined }}>Upload Ticket Prices (Max 50MB)</Button>
                    </Upload>
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    label={<span><span style={{ color: formFontColour, fontWeight: '600' }}>iii.</span> Please upload Revenue Streams (comprehensive list and break-down including sponsorships, etc.)</span>}
                    name="revenue_streams"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                    rules={[{ required: true, message: 'Please upload revenue streams document' }]}
                  >
                    <Upload {...businessPlanUploadProps} style={{ display: 'block', textAlign: 'left' }}>
                      <Button icon={<UploadOutlined />} style={{ borderRadius: '6px', width: isSmall ? '100%' : undefined }}>Upload Revenue Streams (Max 50MB)</Button>
                    </Upload>
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    label={<span><span style={{ color: formFontColour, fontWeight: '600' }}>iv.</span> Please upload Itemised Expenses and Costs (including artist fees, lights, audio, risk assessment, health & safety, backline, venue hire, staff costs, etc.)</span>}
                    name="expenses_costs"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                    rules={[{ required: true, message: 'Please upload expenses and costs document' }]}
                  >
                    <Upload {...businessPlanUploadProps} style={{ display: 'block', textAlign: 'left' }}>
                      <Button icon={<UploadOutlined />} style={{ borderRadius: '6px', width: isSmall ? '100%' : undefined }}>Upload Expenses and Costs (Max 50MB)</Button>
                    </Upload>
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    label={<span><span style={{ color: formFontColour, fontWeight: '600' }}>v.</span> Please upload Projected Profit and Loss Report</span>}
                    name="profit_loss_report"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                    rules={[{ required: true, message: 'Please upload projected profit and loss report' }]}
                  >
                    <Upload {...businessPlanUploadProps} style={{ display: 'block', textAlign: 'left' }}>
                      <Button icon={<UploadOutlined />} style={{ borderRadius: '6px', width: isSmall ? '100%' : undefined }}>Upload Profit and Loss Report (Max 50MB)</Button>
                    </Upload>
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    label={<span><span style={{ color: formFontColour, fontWeight: '600' }}>vi.</span> Please upload Anticipated Audience Document</span>}
                    name="anticipated_audience"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                    rules={[{ required: true, message: 'Please upload anticipated audience document' }]}
                  >
                    <Upload {...businessPlanUploadProps} style={{ display: 'block', textAlign: 'left' }}>
                      <Button icon={<UploadOutlined />} style={{ borderRadius: '6px', width: isSmall ? '100%' : undefined }}>Upload Anticipated Audience (Max 50MB)</Button>
                    </Upload>
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    label={<span><span style={{ color: formFontColour, fontWeight: '600' }}>vii.</span> Declaration of the intention of having an Insurance policy and projected cost of same.</span>}
                    name="insurance_declaration"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                    rules={[{ required: true, message: 'Please upload insurance declaration' }]}
                  >
                    <Upload {...businessPlanUploadProps} style={{ display: 'block', textAlign: 'left' }}>
                      <Button icon={<UploadOutlined />} style={{ borderRadius: '6px', width: isSmall ? '100%' : undefined }}>Upload Insurance Declaration (Max 50MB)</Button>
                    </Upload>
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    label="Please upload Project Management Plan"
                    name="project_management_plan"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                    rules={[{ required: true, message: 'Please upload project management plan' }]}
                  >
                    <Upload {...uploadProps} style={{ display: 'block', textAlign: 'left' }}>
                      <Button icon={<UploadOutlined />} style={{ borderRadius: '6px', width: isSmall ? '100%' : undefined }}>Upload Project Management Plan</Button>
                    </Upload>
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    label="Please upload Detailed Marketing Plan"
                    name="marketing_plan"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                    rules={[{ required: true, message: 'Please upload marketing plan' }]}
                  >
                    <Upload {...uploadProps} style={{display: 'block', textAlign: 'left'}}>
                      <Button icon={<UploadOutlined />} style={{borderRadius: '6px', width: isSmall ? '100%' : undefined}}>Upload Marketing Plan</Button>
                    </Upload>
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    label="Please upload a declaration of the amounts (if any) received via Funding which constitutes State aid, together with a breakdown of the costs claimed under such funding, where applicable. In particular, the applicant must disclose all State aid (including State aid awarded under the GBER and de minimis aid) granted, approved or applied for, that covers the same eligible costs as those being claimed under this Fund. (IF APPLICABLE)"
                    name="state_aid_declaration"
                    valuePropName="fileList"
                    getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                  >
                    <Upload {...uploadProps}>
                      <Button icon={<UploadOutlined />} style={{ width: isSmall ? '100%' : undefined }}>Upload State Aid Declaration (If Applicable)</Button>
                    </Upload>
                  </Form.Item>
                </Col>

                {/* Declarations Section */}
                  <Col xs={24}>
                    {isSmall ? (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginTop: '24px', marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: 10 }}>
                        <SafetyOutlined style={{ fontSize: '20px', color: formFontColour, marginTop: 4 }} />
                        <div style={{ color: formFontColour, fontWeight: 700, fontSize: '16px', wordWrap: 'break-word', whiteSpace: 'normal' }}>
                          Declarations
                        </div>
                      </div>
                    ) : (
                      <Divider orientation="left" style={{ marginTop: '32px', marginBottom: '24px' }}>
                        <Space style={{ alignItems: 'center', gap: 12 }}>
                          <SafetyOutlined style={{ fontSize: '20px', color: formFontColour }} />
                          <span style={{ fontSize: '20px', fontWeight: 700, color: formFontColour, display: 'inline-block', marginLeft: 6 }}>
                            Declarations
                          </span>
                        </Space>
                      </Divider>
                    )}
                  </Col>

                <Col xs={24}>
                  <Form.Item
                    name="declare_health_safety"
                    valuePropName="checked"
                    rules={[{ 
                      validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error('You must agree to health and safety regulations'))
                    }]}
                    style={{ textAlign: 'left' }}
                  >
                    <Checkbox style={{ alignItems: 'flex-start' }}>
                      <strong>We declare that we adhere to all health and safety regulations and laws</strong>
                    </Checkbox>
                  </Form.Item>
                </Col>

                {/* New mandatory declarations */}
                <Col xs={24}>
                  <Form.Item
                    name="declare_not_undertaking_difficulty"
                    valuePropName="checked"
                    rules={[{ 
                      validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error('You must confirm applicants are not undertakings in difficulty'))
                    }]}
                    style={{ textAlign: 'left' }}
                  >
                    <Checkbox style={{ alignItems: 'flex-start' }}>
                      <strong>We declare that we are not considered as undertakings in difficulty as defined by Article 2 (18) of the GBER.</strong>
                    </Checkbox>
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    name="declare_no_outstanding_recovery"
                    valuePropName="checked"
                    rules={[{ 
                      validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error('You must confirm there are no outstanding recovery orders'))
                    }]}
                    style={{ textAlign: 'left' }}
                  >
                    <Checkbox style={{ alignItems: 'flex-start' }}>
                      <strong>We declare that we are not subject to any outstanding recovery orders following a previous Commission decision declaring an aid granted by Malta illegal and incompatible with the internal market.</strong>
                    </Checkbox>
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Form.Item
                    name="declaration_operate"
                    valuePropName="checked"
                    rules={[{ 
                      validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error('You must confirm you are legally entitled to operate in Malta'))
                    }]}
                    style={{ textAlign: 'left' }}
                  >
                    <Checkbox style={{ alignItems: 'flex-start' }}>
                      <strong>We declare that we are legally entitled to operate in Malta for the project</strong>
                    </Checkbox>
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item style={{ textAlign: 'left' }}>
                    <Checkbox disabled style={{ opacity: 0.6, alignItems: 'flex-start' }}>
                      Hard copy of Submission (currently unavailable)
                    </Checkbox>
                  </Form.Item>
                </Col>

                <Col xs={24} md={12}>
                  <Form.Item
                    name="soft_copy_submitted"
                    valuePropName="checked"
                    initialValue={true}
                    style={{ textAlign: 'left' }}
                  >
                    <Checkbox defaultChecked style={{ alignItems: 'flex-start' }}>
                      Soft copy of Submission
                    </Checkbox>
                  </Form.Item>
                </Col>

                <Col xs={24}>
                  <Divider />
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button 
                      className="rnm-primary"
                      type="primary" 
                      htmlType="submit" 
                      size="large" 
                      loading={loading}
                      style={{ 
                        width: '100%', 
                        height: '56px',
                        fontSize: '16px',
                        fontWeight: '600',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(24, 144, 255, 0.12)'
                      }}
                    >
                      Submit Application
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </div>
      </Content>
    </Layout>
    </>
  );
};

export default RockNMalta;